import { PHYSICS_EPSILON } from '@md/shared';

/**
 * Boîtes englobantes alignées sur les axes (AABB).
 *
 * Convention du moteur : `x`/`y` désignent le coin HAUT-GAUCHE, `y` croît vers
 * le BAS (comme les lignes de la carte ASCII), et toutes les grandeurs sont en
 * tuiles (spec §3).
 */
export interface Aabb {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Point en unités tuiles. */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** Recouvrement strict : deux boîtes qui se touchent ne se recouvrent pas. */
export function aabbOverlaps(a: Aabb, b: Aabb): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Contact ou recouvrement : deux boîtes qui se TOUCHENT par une face comptent,
 * contrairement à `aabbOverlaps`. Le contact par un simple coin est exclu (une
 * face commune de longueur nulle n'est pas un contact).
 *
 * C'est ce test qu'utilisent les tuiles solides « actives » — dalle
 * électrifiée, surface d'accroche, dalle d'inversion — puisqu'un robot ne peut
 * jamais recouvrir une tuile solide : il s'arrête exactement sur sa face.
 */
export function aabbTouches(a: Aabb, b: Aabb, epsilon = PHYSICS_EPSILON): boolean {
  const overlapX = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const overlapY = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  if (overlapX < -epsilon || overlapY < -epsilon) {
    return false;
  }
  return overlapX > epsilon || overlapY > epsilon;
}

/** Distance euclidienne entre deux points, en tuiles. */
export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Centre d'une boîte. */
export function aabbCenter(a: Aabb): Vec2 {
  return { x: a.x + a.w / 2, y: a.y + a.h / 2 };
}

/** Le point est-il dans la boîte (bord haut/gauche inclus, bas/droite exclu) ? */
export function aabbContainsPoint(a: Aabb, p: Vec2): boolean {
  return p.x >= a.x && p.x < a.x + a.w && p.y >= a.y && p.y < a.y + a.h;
}

/** Boîte occupée par la tuile de coordonnées entières (`tx`, `ty`). */
export function tileAabb(tx: number, ty: number): Aabb {
  return { x: tx, y: ty, w: 1, h: 1 };
}
